import logoQuilt from './logo/logo.png';
import logoVacquilt from './logo/SalutingService-LogoEN-RGB.svg';
import logoVacquiltFR from './logo/SaluonsLeurService-Logo-FR-RGB.svg';

import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from 'providers/auth-provider';
import { useTranslates } from 'providers/translates';
import { RoutesName } from 'routs';
import { useAppSelector, useRedirectAndScroll } from '../../hooks';
import { getCookie } from '../../utilities';
import { CreateQuiltButton } from './components/CreateQuiltButton';
import { DesktopMenu } from './components/DesktopMenu';
import { MobileMenu } from './components/MobileMenu';
import { isVac } from "utilities/is-vac";
import { vacHeaderLabel } from 'utilities/vac-copy';
import { Helmet } from 'react-helmet-async';
import { Language } from 'providers/translates/enums';
import './header.scss';

const vacLogo = logoVacquilt;
const vacFrLogo = logoVacquiltFR;
const memorialLogo = logoQuilt;

interface HeaderProps {}

const initialHeaderItemsController = {
  language: false,
  howToUse: false,
  support: false,
  myAccount: false,
  donate: false,
  aboutUs: false,
  contactUs: false,
  icons: false,
};

const translationsHelmet = {
  descriptionEN: 'Create a lasting tribute for a Veteran. Share stories, photos, memories, and more in a quilt square that becomes part of the Saluting Service national quilt. Browse quilts. Explore resources.',
  descriptionFR: 'Créez un hommage durable à une vétérane ou un vétéran Partagez des anecdotes, des photos, des souvenirs et bien d’autres choses encore dans un carreau de courtepointe qui fera partie de la courtepointe nationale du site Saluons leur service. Parcourir les carreaux. Explorer les ressources.',
  titleEN: 'Saluting Service | Create a Free Online Tribute',
  titleFR: 'Saluons leur service | Créez gratuitement un hommage en ligne'
} as const;

const Header: FC<HeaderProps> = () => {
  const { translates: t, selectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;
  const isSave = useAppSelector((state) => state.notify.isSave);
  const { onlyRedirect } = useRedirectAndScroll({});
  const [searchParams] = useSearchParams();
  const { user } = useAuth();  
  const location = useLocation();
  const { logout } = useAuth();
  const headerButtonsRef = useRef<HTMLDivElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const [headerItemsController, setHeaderItemsController] = useState(initialHeaderItemsController);


  useEffect(() => {
    if (!headerButtonsRef.current) return;

    const handleStyleChange = () => {
      const element = headerButtonsRef.current;
      if (!element) return;

      if (isMenuOpen) {
        element.style.display = 'flex';
        element.style.opacity = '1';
        element.style.transition = 'opacity 0.3s ease';
      } else {
        element.style.display = 'flex';
        element.style.opacity = '1';
      }
    };

    handleStyleChange();
  }, [isMenuOpen]);

  
  useEffect(() => {
    if (!headerButtonsRef.current) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        setIsMenuOpen(prev => !prev);
      });
    });

    observer.observe(headerButtonsRef.current, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  const actionLogout = () => {
    isSave && logout();
    onlyRedirect('/login');
  };

  const logoSite = useMemo(() => {
    if (!isVac()) {
      return memorialLogo;
    }
    return isFrench ? vacFrLogo : vacLogo;
  }, [selectedLang]);

  useEffect(() => {
    if (user?.isInvitedUser) {
      const quiltId = getCookie('quiltId');
      const step = searchParams.get('step');
      const allowPages = ['login', 'signup', 'howtouse', 'resources', 'search', 'about'];

      if (!quiltId) return;

      const isCanRedirect = allowPages.some(
        (page) => location.pathname.includes(page) || location.pathname === '/'
      );

      if (!step && !isCanRedirect) {
        onlyRedirect(`/update/${quiltId}?step=3`);
      }
    }
  }, [user?.isInvitedUser, location]);

  useEffect(() => {
    if (!location?.pathname) return;

    if (user?.isInvitedUser) {
      setHeaderItemsController({
        language: true,
        howToUse: true,
        support: true,
        myAccount: false,
        donate: false,
        aboutUs: true,
        contactUs: false,
        icons: false,
      });
      return;
    }

    setHeaderItemsController(
      location.pathname === '/' 
        ? {
            language: true,
            howToUse: true,
            support: true,
            myAccount: true,
            donate: true,
            aboutUs: true,
            contactUs: false,
            icons: false,
          }
        : {
            language: true,
            howToUse: true,
            support: true,
            myAccount: true,
            donate: false,
            aboutUs: true,
            contactUs: false,
            icons: false,
          }
    );
  }, [location]);

  return (
    <>
      {isVac() && (
        <Helmet>
          <meta name="description" content={isFrench ? translationsHelmet.descriptionFR : translationsHelmet.descriptionEN} />
          <title>{isFrench ? translationsHelmet.titleFR : translationsHelmet.titleEN}</title>
        </Helmet>
      )}
      
      <header className={`${'header'}${isVac() ? ` isVac` : ``}`}>
        <div className="center header-desktop-menu">
          <DesktopMenu 
            headerItemsController={headerItemsController} 
            actionLogout={actionLogout}
          />
        </div>

        <Box sx={{
          minHeight: '1px',
          background: "url('/img/general/line-small.png') repeat-x",
        }} />

        <div className="center">
          <div className="header__content">
            <div className={`logo-and-menu-toggle${isVac() ? ' isVac' : ''}`}>
              <div 
                onClick={() => onlyRedirect('/')} 
                className="logo" 
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
              >
                <img src={logoSite} alt="logo" />
              </div>
              
              <MobileMenu 
                headerItemsController={headerItemsController} 
                actionLogout={actionLogout}
                headerButtonsRef={headerButtonsRef}
              />
            </div>
            
            <div 
              ref={headerButtonsRef} 
              className="header__wrapper"
              style={{
                display: 'flex',
                transition: 'opacity 0.3s ease'
              }}
            >
              <div className="header__buttons">
                <div
                  className="header__button"
                  onClick={() => onlyRedirect(RoutesName.SearchQuilt)}
                  role="button"
                  tabIndex={0}
                >
                  <span>{vacHeaderLabel(selectedLang, t?.header?.searchQuilts, 'searchQuilts', 'Search Memorials')}</span>
                </div>
                <div className="header__button-delimitiier" />
                <div className="header__button">
                  <CreateQuiltButton />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {isVac() && (
          <h2 className="byline">
            <span className="byline-text">
              {vacHeaderLabel(selectedLang, t?.header?.byline, 'byline', 'Honouring duty and service to country')}
            </span>
          </h2>
        )}
      </header>
    </>
  );
};

export default Header;
